// Display-only identities. Never use these labels as provider caller IDs or
// replace the raw call numbers used by routing, transfers and call history.
export interface IncomingInternalCall {
  callerName: string;
  extensionNumber: string;
}

interface ExtensionIdentity {
  extensionNumber: string;
  targetType: string;
  targetEndpointId?: string | null;
  label?: string | null;
}

interface EndpointIdentity {
  id: string;
  alias?: string | null;
}

interface ExtensionCallDisplayInput {
  direction: "inbound" | "outbound";
  callback: boolean;
  destination: string;
  connectedEndpointId: string | null;
  localName?: string;
  incoming: IncomingInternalCall | null;
  extensions: readonly ExtensionIdentity[];
  endpoints: readonly EndpointIdentity[];
}

function identity(name: string, number?: string) {
  return number ? `Ext ${number}${name ? ` — ${name}` : ""}` : name;
}

export function getExtensionCallDisplay(input: ExtensionCallDisplayInput): { from: string; to: string; title?: string } | null {
  if (input.callback) return null;
  const localEndpoint = input.endpoints.find(ep => ep.id === input.connectedEndpointId);
  const localName = localEndpoint?.alias || input.localName || "You";
  const localExtensions = input.extensions.filter(ext =>
    ext.targetType === "agent" && ext.targetEndpointId === input.connectedEndpointId && !!input.connectedEndpointId,
  );
  // With multiple aliases, no particular originating extension was selected.
  // Show the agent instead of claiming an arbitrary source number.
  const localNumber = localExtensions.length === 1 ? localExtensions[0].extensionNumber : undefined;

  if (input.direction === "inbound") {
    if (!input.incoming) return null;
    const name = input.incoming.callerName || "Agent";
    const callerEndpoints = input.incoming.callerName
      ? input.endpoints.filter(ep => ep.alias === name) : [];
    const callerExtensions = callerEndpoints.length === 1
      ? input.extensions.filter(ext => ext.targetType === "agent" && ext.targetEndpointId === callerEndpoints[0].id)
      : [];
    return {
      from: identity(name, callerExtensions.length === 1 ? callerExtensions[0].extensionNumber : undefined),
      // The internal-call header identifies the called extension. The SIP From
      // value can equal this number and must not be mistaken for the caller.
      to: identity(localName, input.incoming.extensionNumber || localNumber),
    };
  }

  const target = input.extensions.find(ext => ext.extensionNumber === input.destination);
  if (!target || target.targetType !== "agent") return null;
  const targetEndpoint = input.endpoints.find(ep => ep.id === target.targetEndpointId);
  const targetName = targetEndpoint?.alias || target.label || "";
  return {
    from: localName,
    to: identity(targetName, target.extensionNumber),
    title: targetName || `Ext ${target.extensionNumber}`,
  };
}
